import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { AnimatePresence, motion } from "framer-motion";
import { twMerge } from "tailwind-merge";

import type { LatestBlock } from "shared/api/blocks/types";
import type { TransactionQueryResponse } from "shared/api/transactions/useLastTransaction";
import type { TotalTransactionQueryResponse } from "shared/api/transactions/useTotalTransactionByPeriodQuery";
import type { IconName } from "shared/ui/Icon";

import { DashboardTableBlocks } from "features/DashboardTableBlocks";
import { DashboardTableTransactions } from "features/DashboardTableTransactions";
import { useBlocksStore } from "features/useGlobalLatestBlocks";
import { ProposalItem } from "pages/Governance/ui/ProposalItem";
import { useSupplyByDenomQuery } from "shared/api/bank/useSupplyByDenomQuery";
import { useTotalAccountsQuery } from "shared/api/explorer-accounts/useTotalAccountsQuery";
import { useProposalsQuery } from "shared/api/proposals";
import { useStakingPoolQuery } from "shared/api/staking/useStakingPoolQuery";
import { useLastTransactionQuery } from "shared/api/transactions/useLastTransaction";
import { useTotalTransactionByPeriodQuery } from "shared/api/transactions/useTotalTransactionByPeriodQuery";
import { useTotalTransactionQuery } from "shared/api/transactions/useTotalTransactionQuery";
import { fromUnesToNes } from "shared/helpers/fromUnesToNes";
import { getChainById } from "shared/helpers/getChainById";
import { getFormattedNumber } from "shared/helpers/getFormattedNumber";
import { useMinWidthMediaQuery } from "shared/hooks/useMediaQuery";
import { AnimateRoute } from "shared/ui/AnimateRoute";
import { EmptyText } from "shared/ui/EmptyText";
import { Icon } from "shared/ui/Icon";
import { LineChart } from "shared/ui/LineChart";
import { Spinner } from "shared/ui/Spinner";
import { toaster } from "shared/ui/Toast";
import { Tooltip } from "shared/ui/Tooltip";

import bgVideo from "./assets/bg.mp4";
import introVideo from "./assets/intro.mp4";

type StatisticsItem = {
  icon?: IconName;
  title: string;
  value: string;
};

const now = new Date(
  Date.UTC(new Date().getUTCFullYear(), new Date().getUTCMonth(), new Date().getUTCDate()),
);

type DashboardProps = {
  isFirstLoad: boolean;
  onVideoEnd: () => void;
};

export const Dashboard = ({ isFirstLoad, onVideoEnd }: DashboardProps) => {
  const { chainId } = useParams<{ chainId: string }>();
  const navigate = useNavigate();

  const network = getChainById(chainId || "");

  const blocks = useBlocksStore((state: { blocksHistory: LatestBlock[] }) => state.blocksHistory);
  const latestBlock = blocks[0];
  const isLatestLoading = !latestBlock;

  const sm = useMinWidthMediaQuery("sm");

  const { data: latestTransactions, isLoading: isLatestTransactionsLoading } =
    useLastTransactionQuery({ limit: 20, skip: 0 });

  const txs = useMemo(() => {
    const txs = blocks.flatMap(({ latestBlock, time, transactions }) =>
      transactions.map((tx: string) => ({ blockHeight: latestBlock, time, tx })),
    );
    if (txs.length >= 12) return txs;
    const set = new Set(txs.map(({ tx }: { tx: string }) => tx));

    const result: {
      blockHeight: string;
      time: string;
      tx: TransactionQueryResponse | string;
    }[] = [...txs];

    latestTransactions?.list.forEach((tx) => {
      if (!set.has(tx.txhash)) {
        result.push({ blockHeight: tx.tx_response.height, time: tx.tx_response.timestamp, tx });
      }
    });
    return result;
  }, [latestTransactions, blocks]);

  const { data: supply } = useSupplyByDenomQuery({ network });
  const { data: poolData, isLoading: isPoolLoading } = useStakingPoolQuery({ network });

  const { data: totalTransactions } = useTotalTransactionQuery({ refetchInterval: 5 * 1000 });

  const [chartScale, setChartScale] = useState("1");

  const getQueryDateRange = (scale: string) => {
    const endDay = new Date(
      Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - 1),
    );
    const endDate = endDay.toISOString();
    let startDate;
    const minDate = new Date(Date.UTC(2024, 7, 1)); // Month is 0-based, so 7 is August

    switch (scale) {
      case "1":
        startDate = new Date(
          Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - 90),
        ).toISOString();
        break;
      case "7":
        startDate = new Date(
          Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - 180),
        ).toISOString();
        break;
      case "30":
        startDate = new Date(
          Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - 270),
        ).toISOString();
        if (new Date(startDate) < minDate) {
          startDate = minDate.toISOString();
        }
        break;
      default:
        startDate = new Date(
          Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - 90),
        ).toISOString();
    }

    return { endDate, startDate };
  };

  const { data: txChart, isPending: isLoadingTxChart } = useTotalTransactionByPeriodQuery(
    getQueryDateRange(chartScale),
    {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    },
  );

  const txArray = useMemo(() => {
    if (isLoadingTxChart) return [];
    if (!txChart) return [];
    return Object.values(txChart);
  }, [isLoadingTxChart, txChart]);

  const lastItemVolume =
    (txArray[txArray.length - 1] as TotalTransactionQueryResponse)?.sessions ?? 0;
  const oneToLastItemVolume =
    (txArray[txArray.length - 2] as TotalTransactionQueryResponse)?.sessions ?? 0;
  const requestsDelta =
    txArray.length >= 3 && lastItemVolume && oneToLastItemVolume
      ? (lastItemVolume - oneToLastItemVolume) / oneToLastItemVolume
      : 0;

  const { data: totalAccounts } = useTotalAccountsQuery({
    network,
  });

  const { data: proposals, isLoading: isProposalsLoading } = useProposalsQuery({ network });

  const filtered = proposals?.proposals.filter(
    (el) => el.status === "PROPOSAL_STATUS_VOTING_PERIOD",
  );

  const statisticsItems: StatisticsItem[] = [
    { icon: "layers", title: "Block Height", value: getFormattedNumber(latestBlock?.latestBlock) },
    { icon: "scanEye", title: "Validators", value: getFormattedNumber(latestBlock?.validators) },
    {
      icon: "circleDot",
      title: "Bonded Tokens",
      value: poolData?.pool.bonded_tokens
        ? getFormattedNumber(fromUnesToNes(poolData?.pool.bonded_tokens).toNumber())
        : poolData?.pool.bonded_tokens ?? "-",
    },
    {
      icon: "database",
      title: "Supply",
      value: supply?.amount?.amount
        ? getFormattedNumber(fromUnesToNes(supply.amount.amount).toNumber())
        : "-",
    },
    {
      icon: "arrowLeftRight",
      title: "Total Transactions",
      value: totalTransactions ? getFormattedNumber(totalTransactions.total) : "-",
    },
    {
      icon: "user",
      title: "Total Accounts",
      value: totalAccounts ? getFormattedNumber(totalAccounts) : "-",
    },
  ];

  const footerItems: StatisticsItem[] = [
    { title: "nesa.ai", value: "Main Site" },
    { title: "beta.nesa.ai", value: "Playground" },
    { title: "docs.nesa.ai", value: "Docs" },
    {
      title: "nesa.ai/token",
      value: "Token",
    },
    {
      title: "nesa.ai/terms",
      value: "Terms",
    },
    {
      title: "nesa.ai/privacy",
      value: "Privacy",
    },
  ];

  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const [isVideoFading, setIsVideoFading] = useState(false);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);

  return (
    <AnimateRoute className="flex w-full flex-col">
      <div className={twMerge("relative flex flex-col gap-10 sm:pl-14")}>
        <AnimatePresence mode="wait">
          {isFirstLoad ? (
            <motion.div
              animate={{ opacity: isVideoLoaded ? 1 : 0 }}
              className="flex flex-col gap-10 pb-28 pl-6 pt-14 sm:pl-0"
              exit={{ opacity: 0 }}
              initial={{ opacity: 0 }}
              key="intro"
              transition={{ duration: 0.3 }}
            >
              <span className="z-10 w-fit rounded-full bg-clay-900 px-3 py-2 text-sm text-tusk-100">
                Announcement
              </span>

              <motion.video
                animate={{ opacity: isVideoFading ? 0 : 1 }}
                autoPlay
                className="absolute left-0 top-0 size-full object-cover"
                id="intro"
                initial={{ opacity: 0 }}
                muted
                onEnded={() => {
                  setIsVideoFading(true);
                  setTimeout(() => {
                    onVideoEnd();
                  }, 200);
                }}
                onLoadedData={() => setIsVideoLoaded(true)}
                playsInline
                src={introVideo}
                transition={{ duration: 0.2 }}
              ></motion.video>

              <span className="z-10 -mt-4 mb-3 text-3xl text-white sm:text-6xl">
                <span className="text-tusk-100">NES</span> is now listed.
              </span>
            </motion.div>
          ) : (
            <motion.div
              animate={{ opacity: 1 }}
              className="flex flex-col pt-2 sm:flex-row sm:items-center"
              exit={{ opacity: 0 }}
              initial={{ opacity: 0 }}
              key="bg"
              transition={{ duration: 0.5 }}
            >
              <div className="flex flex-col px-6 sm:px-0">
                <div className="flex items-center text-sm font-medium text-white">
                  <Icon
                    className="mr-4 size-8 rounded-full bg-clay-900 p-2 text-tusk-100"
                    name="logo"
                  />
                  NESA AI&nbsp;<span className="text-clay-300">($NES)</span>
                </div>

                <div
                  className="mt-2 line-clamp-2 flex w-52 text-xl text-white sm:w-72 sm:text-3xl"
                  onBlur={() => !sm && setIsTooltipOpen(false)}
                  tabIndex={0}
                >
                  <span
                    className="line-clamp-2 flex items-center"
                    onBlur={() => !sm && setIsTooltipOpen(false)}
                    onClick={() => !sm && setIsTooltipOpen(!isTooltipOpen)}
                  >
                    On-chain AI{" "}
                    <Tooltip
                      className="inline-block w-52 bg-clay-900 p-3 pr-0"
                      content={
                        <div className="text-xs font-light text-clay-100">
                          Showing the total number of <span className="font-bold">AI Requests</span>{" "}
                          settled on Nesa Chain
                        </div>
                      }
                      open={!sm ? isTooltipOpen : undefined}
                      side="top"
                    >
                      <Icon
                        className="ml-1 inline-block size-6 align-middle text-clay-300 max-md:mt-[2px] max-md:size-5 sm:mt-1"
                        name="circleAlert"
                      />
                    </Tooltip>
                  </span>
                </div>

                <div className="absolute right-8 top-4 flex flex-col gap-3 sm:static sm:mt-5 sm:flex-row sm:items-center">
                  <Icon
                    className="size-5 rounded-md bg-tusk-100 p-1 text-black sm:size-8 sm:p-2"
                    name={requestsDelta >= 0 ? "arrowUp" : "arrow_downward"}
                  />
                  <div className="flex h-full flex-col justify-between text-base/none text-tusk-100">
                    <span className="translate-y-[2px] max-md:translate-y-[-3px]">
                      {requestsDelta > 0 && "+"}
                      {((requestsDelta ?? 0) * 100).toFixed(2)}%
                    </span>
                    <span className="mt-1 text-[0.625rem] text-clay-300 sm:text-xs">
                      Requests Change 24H
                    </span>
                  </div>
                </div>

                <div className="mt-5 flex gap-4 sm:gap-2">
                  <div
                    className="flex flex-1 cursor-pointer items-center justify-between rounded-lg bg-clay-900 px-3 py-1.5 text-clay-300 sm:w-fit"
                    onClick={() => {
                      toaster.info("Coming soon");
                    }}
                  >
                    <span>
                      Buy&nbsp;<span className="text-white">$NES</span>
                    </span>
                    <Icon className="size-5 p-0" name="arrowDownSm" />
                  </div>

                  <div
                    className="flex flex-1 cursor-pointer items-center justify-between rounded-lg border border-clay-900 px-3 py-1.5 text-sm text-white sm:w-fit"
                    onClick={() => {
                      toaster.info("Coming soon");
                    }}
                  >
                    Stake Now
                    <Icon className="size-5 p-0 text-clay-300" name="arrowDownSm" />
                  </div>
                </div>
              </div>

              <div className="flex w-full flex-col gap-5">
                {sm && (
                  <div className="ml-10 flex h-8 gap-6">
                    {statisticsItems.map((item) => (
                      <div className="flex gap-4" key={item.title}>
                        <Icon
                          className="size-8 rounded-lg bg-clay-900 p-2 text-tusk-100"
                          name={item.icon as IconName}
                        />

                        <div className="flex flex-col justify-between">
                          <span className="text-base/none text-white">
                            {isLatestLoading ? <Spinner className="size-4" /> : item.value}
                          </span>
                          <span className="text-xs/none text-clay-380">{item.title}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div className="h-60 w-full">
                  {isLoadingTxChart ? (
                    <div className=" flex h-44 items-center justify-center">
                      <Spinner className=" size-8" />
                    </div>
                  ) : txChart && Object.keys(txChart).length ? (
                    <LineChart data={txChart} onScaleChange={setChartScale} scale={chartScale} />
                  ) : (
                    <EmptyText>Empty</EmptyText>
                  )}
                </div>

                {!sm && (
                  <div className="mb-4 flex h-8 w-full gap-6 overflow-scroll px-6 scrollbar-none sm:pl-10">
                    {statisticsItems.map((item) => (
                      <div className="flex gap-4" key={item.title}>
                        <Icon
                          className="size-8 shrink-0 rounded-lg bg-clay-900 p-2 text-tusk-100"
                          name={item.icon as IconName}
                        />

                        <div className="flex w-full flex-col justify-between">
                          <span className="text-sm/none text-white">
                            {isLatestLoading ? <Spinner className="size-4" /> : item.value}
                          </span>
                          <span className="whitespace-nowrap text-xs/none text-clay-380 ">
                            {item.title}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <div className="mx-6 border-b border-clay-900 sm:mx-0"></div>

      <div className="mt-9 grid grid-cols-1 gap-8 px-6 sm:mt-12 sm:grid-cols-2 sm:px-16">
        <div>
          <div className="flex flex-col items-center justify-between gap-3 pb-4 text-xl/none text-white sm:flex-row sm:pb-8 sm:text-3xl/none">
            Latest Transactions
            <div
              className="flex cursor-pointer items-center text-sm text-clay-380"
              onClick={() => navigate(`/${network.chainId}/transactions`)}
            >
              View All Transactions
              <Icon className="size-4 text-white" name="arrowUpRight" />
            </div>
          </div>

          {isLatestTransactionsLoading ? (
            <Spinner className="size-7" />
          ) : (
            <DashboardTableTransactions transactionsCount={12} txs={txs} />
          )}
        </div>

        <div>
          <div className="flex flex-col items-center justify-between gap-3 pb-4 text-xl/none text-white sm:flex-row sm:pb-8 sm:text-3xl/none">
            Latest Blocks
            <div
              className="flex cursor-pointer items-center text-sm text-clay-380"
              onClick={() => navigate(`/${network.chainId}/blocks`)}
            >
              View All Blocks
              <Icon className="size-4 text-white" name="arrowUpRight" />
            </div>
          </div>
          {isLatestLoading ? (
            <Spinner className="size-7" />
          ) : (
            <DashboardTableBlocks blocksCount={12} />
          )}
        </div>
      </div>

      <div className="mt-10 px-4 sm:px-16">
        <div className="flex flex-col items-start justify-between gap-3 pb-4 text-lg/none text-white sm:flex-row sm:pb-8 sm:text-3xl/none">
          Active Proposals
          <div
            className="flex cursor-pointer items-center text-sm text-clay-380"
            onClick={() => {
              toaster.info("Coming Soon");
            }}
          >
            View All Proposals
            <Icon className="size-4 text-white" name="arrowUpRight" />
          </div>
        </div>

        {(isProposalsLoading || isPoolLoading) && <Spinner className="size-7" />}
        {!isProposalsLoading && !isPoolLoading && (
          <div className="mb-10 rounded-lg bg-clay-1100">
            {filtered?.length ? (
              filtered.map((proposal) => (
                <ProposalItem key={proposal.id} poolData={poolData} proposal={proposal} />
              ))
            ) : (
              <EmptyText className="py-7">No Active Proposals yet</EmptyText>
            )}
          </div>
        )}
      </div>

      <div className="relative flex flex-col gap-10 overflow-hidden bg-[#010101] pb-9 pl-4 pt-16 sm:pl-20">
        <video
          autoPlay
          className="absolute left-[180px] top-0 size-full object-cover object-[100%_28%]"
          id="bg"
          loop
          muted
          playsInline
          src={bgVideo}
        ></video>

        <span className="z-10 text-3xl text-white sm:text-6xl">
          Witness the Power of <span className="text-tusk-100">Trusted AI</span> on Chain
        </span>

        <div className="z-10 grid h-8 grid-cols-3 gap-2 sm:flex sm:gap-6">
          {footerItems.map((item) => (
            <a
              className="flex gap-4"
              href={`https://${item.title == "nesa.ai/privacy" ? "nesa.ai/privacy-policy" : item.title}`}
              key={item.title}
              rel="noreferrer"
              target="_blank"
            >
              {sm && (
                <Icon
                  className="size-8 rounded-lg bg-clay-900 p-2 text-white"
                  name="arrowUpRight"
                />
              )}
              <div className="flex flex-col justify-between">
                <span className="text-xs text-white sm:text-base/none">
                  {isLatestLoading ? <Spinner className="size-4" /> : item.value}
                </span>
                <span className="text-xs/none text-clay-380">{item.title}</span>
              </div>
            </a>
          ))}
        </div>

        <span className="z-10 text-xs font-light text-clay-380 max-md:mt-7 max-md:text-[10px]">
          Copyright 2025 Nesa.
        </span>
      </div>
    </AnimateRoute>
  );
};
